import { useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router';
import { ShippingRestrictionScope } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { useForm, useToastStatus } from 'commons/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useDisabledShippingMethods } from './useDisabledShippingMethods';
import {
  useCreateShippingRestriction,
  useUpdateShippingRestriction,
  useGetShippingRestrictionDetail,
  useShippingMethodsOptions,
  useGetShippingRestrictions,
} from '../../../hooks';
import { DELIVERY_METHODS_ROUTES } from '../../../shippingRoutes';
import { ShippingRestrictionFormValues } from '../types';
import {
  SHIPPING_RESTRICTION_INITIAL_VALUES,
  hasAtLeastOneRule,
} from '../utils';
import { createValidationSchema } from '../validations';

const SKELETON_TITLE = 'skeleton' as const;

interface UseShippingRestrictionPageOptions {
  mode: 'create' | 'edit';
}

interface UseShippingRestrictionPageReturn {
  values: ShippingRestrictionFormValues;
  handleOnSubmit: (
    e?: React.SyntheticEvent<HTMLButtonElement | HTMLFormElement, Event>,
  ) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (name: string, value: unknown) => void;
  errors: Partial<Record<keyof ShippingRestrictionFormValues, string>>;
  isUpdating: boolean;
  isError: boolean;
  title: string;
  currentRestrictionId?: string;
  showSkeleton: boolean;
  isConfirmDisabled: boolean;
}

export function useShippingRestrictionPage({
  mode,
}: UseShippingRestrictionPageOptions): UseShippingRestrictionPageReturn {
  const { id: carrierId, restrictionId } = useParams<{
    id: string;
    restrictionId?: string;
  }>();
  const { goTo } = useNavegate();
  const t = useTranslationShipping('deliveryMethods.shippingLimiting');

  const {
    create,
    isLoading: isCreating,
    status: createStatus,
  } = useCreateShippingRestriction();

  const {
    update,
    isLoading: isUpdating,
    status: updateStatus,
  } = useUpdateShippingRestriction();

  const {
    restriction,
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
    fetchDetail,
  } = useGetShippingRestrictionDetail();

  const { methods } = useShippingMethodsOptions(carrierId);
  const { restrictions } = useGetShippingRestrictions();

  const { isMethodDisabled } = useDisabledShippingMethods({
    restrictions: restrictions || [],
    currentRestrictionId: restrictionId,
  });

  useEffect(() => {
    if (mode === 'edit' && restrictionId) {
      fetchDetail(restrictionId);
    }
  }, [mode, restrictionId, fetchDetail]);

  const initialValues = useMemo(() => {
    if (mode === 'edit' && restriction) {
      return restriction;
    }

    return {
      ...SHIPPING_RESTRICTION_INITIAL_VALUES,
      methodId: `api_${carrierId}`,
    };
  }, [carrierId, mode, restriction]);

  const handleSubmit = useCallback(
    async (formValues: ShippingRestrictionFormValues) => {
      const adjustedValues = { ...formValues };

      if (
        adjustedValues.shippingMethodsScope === ShippingRestrictionScope.ALL
      ) {
        const availableMethods = methods.filter(
          (method) => !isMethodDisabled(method),
        );

        if (
          availableMethods.length > 0 &&
          availableMethods.length < methods.length
        ) {
          const availableMethodCodes = availableMethods.map((m) => m.code);
          adjustedValues.shippingMethodsScope =
            ShippingRestrictionScope.SPECIFIC;
          adjustedValues.selectedShippingMethods = availableMethodCodes;
        }
      }

      if (mode === 'create') {
        await create(adjustedValues);
      } else if (mode === 'edit' && restrictionId) {
        await update(restrictionId, adjustedValues);
      }
    },
    [mode, create, update, restrictionId, methods, isMethodDisabled],
  );

  const { values, handleOnSubmit, handleChange, setFieldValue, errors } =
    useForm<ShippingRestrictionFormValues>({
      initialValues,
      validationSchema: createValidationSchema(),
      onSubmit: handleSubmit,
    });

  const status = mode === 'create' ? createStatus : updateStatus;
  const title = mode === 'create' ? t('form.titleNew') : t('form.titleEdit');

  useToastStatus({
    status,
    success:
      mode === 'create' ? t('form.successCreate') : t('form.successUpdate'),
    error: mode === 'create' ? t('form.errorCreate') : t('form.errorUpdate'),
    onSuccess: () => {
      goTo(
        DELIVERY_METHODS_ROUTES.carrierRestrictions.replace(':id', carrierId),
      );
    },
  });

  const hasError =
    mode === 'edit' ? isErrorDetail || status === 'error' : status === 'error';
  const showSkeleton = mode === 'edit' && isLoadingDetail;

  const hasRules = useMemo(() => hasAtLeastOneRule(values), [values]);

  return {
    values,
    handleOnSubmit,
    handleChange,
    setFieldValue,
    errors,
    isUpdating: mode === 'create' ? isCreating : isUpdating,
    isError: hasError,
    title: showSkeleton ? SKELETON_TITLE : title,
    currentRestrictionId: restrictionId,
    showSkeleton,
    isConfirmDisabled: !hasRules,
  };
}

export default useShippingRestrictionPage;
